// src/auth/screens/AcceptInvitationScreen.jsx
//
// /invitations/accept?token=… — the page an invitation mail links to.
//
// ⚠️ THE PATH IS BACKEND CONFIGURATION. `invitation_url` is a COMPLETE absolute
// URL built by the backend (committed example
// `http://localhost:3000/invitations/accept?token=$token`), so renaming this
// route 404s every mail already in flight — exactly the pairing
// `password_reset_url` / `set_password_url` already have.
//
// ⛔ THE TOKEN IS SINGLE USE, and the loser of a double POST reads
// `ALREADY_USED` — i.e. the page would tell the invited person their link was
// already spent, on the very request that spent it. Two guards, because they
// cover different things:
//   · `acceptInvitationOnce` de-duplicates CONCURRENT calls (React StrictMode
//     runs every effect twice in development, and StrictMode is on in
//     `src/index.js`);
//   · `startedRef` here stops a SECOND call after one has SETTLED. The in-flight
//     map drops its entry on settle by design, so it cannot survive the
//     re-render a successful accept triggers — only this flag can.
// Only [Riprova] lowers the guard again.
//
// ⛔ NO AUTOMATIC NAVIGATION ON SUCCESS. Telling the user what happened is the
// whole point of the page: a silent jump into a workspace they have never seen —
// or, on a company invitation with no active workspace yet, into /no-access —
// reads as a failure right after a success. The success panel carries ONE
// explicit button, and which one comes from a BEFORE/AFTER diff of the tenant
// ids: `TenantInfo` has no `customer_id`, so "the only workspace in the list"
// would happily open an unrelated one.
//
// ⛔ The parked token is PER TAB (`pendingInvitation.js`). A signed-out visitor
// is sent to /login with a notice and `login()` resumes it; a password reset
// opens a new tab, which does not inherit it, so the notice says to reopen the
// invitation link (valid 7 days).
//
// ⛔ NEVER render the server's own sentence. `buildApiError` always sets a
// message on the Error and it is the backend's English, so a fallback to it is
// both reachable and wrong on an Italian-market surface. Every branch here is on
// `err.code` (plus `err.reason`), and every sentence rendered is ours.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthCard, { AuthNotice } from './AuthCard.jsx';
import { useAuth } from '../AuthContext.jsx';
import { acceptInvitationOnce } from '../invitationApi.js';
import { serverReferenceCopy } from '../authErrorCopy.js';
import {
  clearPendingInvitation,
  isPlausibleInvitationToken,
  readPendingInvitation,
  writePendingInvitation,
} from '../pendingInvitation.js';
import { COLORS } from '../../constants/theme.js';

// error_code → copy key. `INVITATION_INVALID` is absent on purpose: it is the
// one code whose wording depends on `reason` (see `errorKey`).
const ERROR_COPY = {
  INVITATION_NOT_FOR_YOU:      'notForYou',
  INVITATION_REVOKED:          'revoked',
  INVITATION_TARGET_INACTIVE:  'targetInactive',
  CANNOT_ATTACH_SUPERADMIN:    'superadmin',
  USER_SEAT_LIMIT_REACHED:     'seatLimit',
  NETWORK:                     'network',
};

// Refusals that leave the invitation ALIVE, so the panel offers [Riprova] — a
// freed seat or a healthy network makes the same link work. Any 5xx joins them.
const RETRYABLE_CODES = new Set(['USER_SEAT_LIMIT_REACHED', 'NETWORK']);

// Refusals that mean the parked copy is dead weight. Dropping it matters beyond
// tidiness: a token that outlives its usefulness hijacks the user's NEXT
// sign-in into this page (login() resumes whatever is parked).
const CLEAR_ON_CODES = new Set([
  'INVITATION_INVALID',
  'INVITATION_REVOKED',
  'INVITATION_TARGET_INACTIVE',
  'CANNOT_ATTACH_SUPERADMIN',
]);

function errorKey(err) {
  if (err.code === 'INVITATION_INVALID') {
    // ⛔ ONLY `ALREADY_USED` means the link was spent. A link superseded by a
    // newer invitation reads `UNKNOWN`, and one whose address was corrected
    // since reads `EXPIRED` — wording either as "already used" would send that
    // person hunting for a sign-in that never happened.
    return err.reason === 'ALREADY_USED' ? 'alreadyUsed' : 'invalid';
  }
  return ERROR_COPY[err.code] || null; // null → the generic copy, with the code
}

// Resolve the token ONCE, at mount. A token in the query string is parked
// immediately, because the next thing this page does is strip the query.
function resolveToken(raw) {
  if (raw) {
    // An implausible handle is a mangled or hand-edited link. Say so WITHOUT
    // spending a request on it — the same no-network principle as a missing one.
    if (!isPlausibleInvitationToken(raw)) return { token: null, fatal: 'invalid', fromUrl: true };
    writePendingInvitation(raw);
    return { token: raw, fatal: null, fromUrl: true };
  }
  const parked = readPendingInvitation();
  if (parked) return { token: parked, fatal: null, fromUrl: false };
  return { token: null, fatal: 'missing', fromUrl: false };
}

export default function AcceptInvitationScreen() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { phase, tenants, applySession, selectTenant, logout } = useAuth();

  const [{ token, fatal, fromUrl }] = useState(() => resolveToken(searchParams.get('token')));
  const [status, setStatus] = useState('idle');   // idle | accepting | done | error
  const [result, setResult] = useState(null);     // { resp, added[] }
  const [failure, setFailure] = useState(null);   // the Error, worded at render

  // Live values the ONE request needs, behind a ref so they are not effect
  // dependencies: a new `applySession` identity or a refreshed tenant list must
  // never look like a reason to POST a single-use token again.
  const liveRef = useRef(null);
  liveRef.current = { tenants, applySession, logout };

  const startedRef = useRef(false);   // one POST per attempt
  const strippedRef = useRef(false);  // one query-strip navigation
  // "Still mounted?" — re-armed on setup so StrictMode's simulated remount does
  // not permanently mark a live component as gone.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const accept = useCallback(async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setStatus('accepting');
    setFailure(null);

    // The diff basis. Taken HERE, immediately before the POST, so it is the
    // list as it stood without the invitation.
    const before = new Set((liveRef.current.tenants || []).map(x => x.tenant_id));

    try {
      const resp = await acceptInvitationOnce(token);
      if (!aliveRef.current) return;
      // Adopt the re-minted session first: the button below opens a workspace
      // that only these claims contain.
      liveRef.current.applySession(resp);
      clearPendingInvitation();
      setResult({
        resp,
        added: (resp.tenants || []).filter(x => !before.has(x.tenant_id)),
      });
      setStatus('done');
    } catch (err) {
      if (!aliveRef.current) return;
      if (err.status === 401) {
        // The session died while the invitation was in hand. Keep the token and
        // send the user back through /login, which resumes it. No panel: the
        // sign-out navigates away.
        liveRef.current.logout({ notice: 'invitation' });
        return;
      }
      if (CLEAR_ON_CODES.has(err.code)) clearPendingInvitation();
      setFailure(err);
      setStatus('error');
    }
  }, [token]);

  useEffect(() => {
    if (fatal) return;
    if (phase === 'login') {
      // ONE navigation, and no request: the accept endpoint needs a bearer for
      // the INVITED account. The token is already parked.
      navigate('/login', { replace: true, state: { notice: 'invitation' } });
      return;
    }
    if (fromUrl && !strippedRef.current) {
      strippedRef.current = true;
      // ⚠️ The token is a credential: get it out of the URL (and out of the
      // history entry) before it is spent.
      navigate('/invitations/accept', { replace: true });
    }
    accept();
  }, [fatal, fromUrl, phase, navigate, accept]);

  function handleRetry() {
    startedRef.current = false;
    setFailure(null);
    setStatus('idle');
    accept();
  }

  function continueWithout() {
    clearPendingInvitation();
    navigate('/', { replace: true });
  }

  // Every refusal panel carries it, the two decided locally included, so a
  // stale token can never trap a later sign-in.
  const continueLink = (
    <div style={{ marginTop: '20px', textAlign: 'center' }}>
      <button type="button" className="auth-link" onClick={continueWithout}>
        {t('invitation.continueWithout')}
      </button>
    </div>
  );

  // ── A dead link, decided without a request ──────────────────────────
  if (fatal) {
    return (
      <AuthCard title={t('invitation.title')}>
        <AuthNotice tone="error">{t(`invitation.${fatal}`)}</AuthNotice>
        {continueLink}
      </AuthCard>
    );
  }

  // ── Accepted ─────────────────────────────────────────────────────────
  // `already_member: true` takes the same paths: the session is adopted either way.
  if (status === 'done' && result) {
    const { resp, added } = result;
    let body;
    let extra = null;
    let button = null;

    if (resp.kind === 'customer') {
      const company = (resp.customers || []).find(c => c.customer_id === resp.target_id);
      body = t('invitation.accepted.customer', { name: company?.legal_name || resp.target_id });
      if (added.length === 1) {
        const only = added[0];
        button = {
          label: t('invitation.accepted.open', { name: only.display_name || only.tenant_id }),
          onClick: () => selectTenant(only, { to: '/' }),
        };
      } else if (added.length > 1) {
        button = {
          label: t('invitation.accepted.chooseWorkspace'),
          onClick: () => navigate('/select-tenant', { replace: true }),
        };
      } else {
        // A viewer of the company, or a company whose workspaces are all
        // retired / not created yet. ⛔ Never /no-access after a success.
        extra = t('invitation.accepted.customerNoWorkspace');
        if ((resp.tenants || []).length > 0) {
          button = {
            label: t('invitation.accepted.backToApp'),
            onClick: () => navigate('/', { replace: true }),
          };
        }
      }
    } else {
      const target = (resp.tenants || []).find(x => x.tenant_id === resp.target_id);
      const name = target?.display_name || resp.target_id;
      body = t('invitation.accepted.tenant', { name });
      button = {
        label: t('invitation.accepted.open', { name }),
        // The object when we have it: `selectTenant` would otherwise resolve the
        // id against a tenant list this render's closure predates.
        onClick: () => selectTenant(target || resp.target_id, { to: '/' }),
      };
    }

    return (
      <AuthCard title={t('invitation.accepted.title')}>
        <AuthNotice tone="success">{body}</AuthNotice>
        {extra && (
          <p style={{ color: COLORS.textSecondary, fontSize: '13px', lineHeight: 1.5, margin: '0 0 18px' }}>
            {extra}
          </p>
        )}
        {button && (
          <button type="button" className="auth-submit" onClick={button.onClick}>
            {button.label}
          </button>
        )}
      </AuthCard>
    );
  }

  // ── Refused ──────────────────────────────────────────────────────────
  if (status === 'error' && failure) {
    const key = errorKey(failure);
    const canRetry = RETRYABLE_CODES.has(failure.code) || failure.status >= 500;
    return (
      <AuthCard title={t('invitation.title')}>
        <AuthNotice tone="error">
          {key
            ? t(`invitation.${key}`)
            // A 5xx with a request_id quotes that reference — more use to support than INTERNAL_ERROR.
            : serverReferenceCopy(t, failure) || t('invitation.generic', { code: failure.code || failure.status || '?' })}
        </AuthNotice>
        {canRetry && (
          <button type="button" className="auth-submit" onClick={handleRetry}>
            {t('invitation.retry')}
          </button>
        )}
        {failure.code === 'INVITATION_NOT_FOR_YOU' && (
          <button
            type="button"
            className="auth-submit"
            style={{ marginTop: canRetry ? '12px' : 0 }}
            onClick={() => logout({ notice: 'invitation' })}
          >
            {t('invitation.switchAccount')}
          </button>
        )}
        {continueLink}
      </AuthCard>
    );
  }

  // ── In flight (and the instant before the /login hand-off) ───────────
  return (
    <AuthCard title={t('invitation.title')}>
      <p role="status" style={{ color: COLORS.textSecondary, fontSize: '13px', lineHeight: 1.5, margin: 0 }}>
        {t('invitation.accepting')}
      </p>
    </AuthCard>
  );
}
